import React from 'react';

import { useSplunkTheme, SplunkThemeProvider } from '@splunk/themes';

export const FONT_FAMILY =
    '"Splunk Platform Sans", "Splunk Data Sans", Roboto, Droid, "Helvetica Neue", Helvetica, Arial, sans-serif';
const LABELFONT = `12px ${FONT_FAMILY}`;
const TITLEFONT = `12px ${FONT_FAMILY}`;
const AXISGRIDSTROKELIGHT = 'rgba(2, 16, 31, 0.2)'; // #02021F 20%
const AXISGRIDSTROKEDARK = 'rgba(212, 226, 241, 0.3)'; // #D4E2F1 30%

const LABELSTROKELIGHT = 'rgba(25, 30, 36, 0.95)'; // #191E24 95%
const LABELSTROKEDARK = 'rgba(255, 255, 255, 0.95)'; // #FFFFFF 95%
const SPACINGCOLORLIGHT = '#FFFFFF';
const SPACINGCOLORDARK = '#000000';

/** Figma: x-axis hover vertical guide — Prisma `borderColorStrong` (#6b6b6b) @ 75% (`tooltip.lineStroke`) */
const VERTICAL_GUIDE_STROKE_LIGHT = 'rgba(107, 107, 107, 0.75)';
/** Figma: x-axis hover vertical guide — Prisma `borderColorStrong` (#97989b) @ 75% (`tooltip.lineStroke`) */
const VERTICAL_GUIDE_STROKE_DARK = 'rgba(151, 152, 155, 0.75)';

const LIGHT_SEVERITY_COLORS = {
    critical: '#E00000',
    major: '#C97705',
    minor: '#155A4E',
    warning: '#006BE5',
    info: '#7B4DF5',
} as const;

const DARK_SEVERITY_COLORS = {
    critical: '#FF4242',
    major: '#F49106',
    warning: '#61CAFA',
    minor: '#2CBDA3',
    info: '#AB74F1',
} as const;

export type VizThemeKey = 'light' | 'dark';

export type VizTheme = {
    name: VizThemeKey;
    color: string;
    font: string;
    labelFont: string;
    legendLabel: string;
    legendLabelFont: string;
    markerFill: string;
    axis: {
        axisStroke: string;
        gridStroke: string;
        xLabelStroke: string;
        yLabelStroke: string;
        titleFont: string;
    };
    tooltip: {
        lineStroke: string;
        inactiveLineStroke: string;
        backgroundColor: string;
        color: string;
        secondaryColor: string;
    };
    pin: {
        iconStroke: string;
        lineStroke: string;
        dotFill: string;
    };
    // Used for Heatmap. The existing 'axis.axisStroke' is used in uPlot.tsx and
    // the values are the same for both light and dark themes
    heatmap: {
        axisStroke: string;
        axisFontSize: number;
        colorScheme: string[];
    };
    // Used to style annotation icons based on the type and severity.
    annotationIcon: {
        customEventIconColor: string;
        alertIconColor: {
            info: string;
            warning: string;
            critical: string;
            major: string;
            minor: string;
        };
    };
    // Used to style alert bar based on the severity
    alertBar: {
        backgroundColor: string;
        severityColors: {
            critical: string;
            major: string;
            warning: string;
            minor: string;
            info: string;
        };
        spacingColor: string;
    };
    // Used for range selection overlay background and border
    rangeSelection: {
        overlayBackgroundColor: string;
        overlayBorderColor: string;
    };
};

// Base shared properties
const FONT_BASE = `12px ${FONT_FAMILY}`;
const HEATMAP_FONT_SIZE = 12;

// Common color palettes
const LIGHT_HEATMAP_COLORS = [
    '#ffffff',
    '#ccd6d7',
    '#aec8cd',
    '#8fb8c7',
    '#6ea8c1',
    '#4b96bd',
    '#2181bb',
    '#0067ba',
    '#0047bd',
    '#0502ca',
];

const DARK_HEATMAP_COLORS = [
    '#000000',
    '#15355a',
    '#315272',
    '#496d89',
    '#60869e',
    '#759db3',
    '#89b4c7',
    '#9ccad9',
    '#afdfec',
    '#c2f5ff',
];

// Pre-defined theme objects to eliminate duplication and runtime creation
const lightVizTheme: VizTheme = {
    name: 'light',
    color: '#2C2C2C',
    font: FONT_BASE,
    labelFont: LABELFONT,
    legendLabel: LABELSTROKELIGHT,
    legendLabelFont: TITLEFONT,
    markerFill: '#FFFFFF',
    axis: {
        axisStroke: AXISGRIDSTROKELIGHT,
        gridStroke: AXISGRIDSTROKELIGHT,
        xLabelStroke: LABELSTROKELIGHT,
        yLabelStroke: LABELSTROKELIGHT,
        titleFont: TITLEFONT,
    },
    tooltip: {
        lineStroke: VERTICAL_GUIDE_STROKE_LIGHT,
        inactiveLineStroke: '#999',
        backgroundColor: 'rgba(44, 44, 44, 0.85)',
        color: 'white',
        secondaryColor: '#999',
    },
    pin: {
        iconStroke: 'white',
        lineStroke: '#9e9e9e',
        dotFill: '#9e9e9e',
    },
    heatmap: {
        axisStroke: '#2c2c2c',
        axisFontSize: HEATMAP_FONT_SIZE,
        colorScheme: LIGHT_HEATMAP_COLORS,
    },
    annotationIcon: {
        customEventIconColor: '#818285',
        alertIconColor: LIGHT_SEVERITY_COLORS,
    },
    alertBar: {
        backgroundColor: '#e6e6e6',
        severityColors: LIGHT_SEVERITY_COLORS,
        spacingColor: SPACINGCOLORLIGHT,
    },
    rangeSelection: {
        overlayBackgroundColor: 'rgba(255, 255, 255, 0.75)',
        overlayBorderColor: '#6B6B6B',
    },
};

const darkVizTheme: VizTheme = {
    name: 'dark',
    color: '#FAFAFA',
    font: FONT_BASE,
    labelFont: LABELFONT,
    legendLabel: LABELSTROKEDARK,
    legendLabelFont: TITLEFONT,
    markerFill: '#000000',
    axis: {
        axisStroke: AXISGRIDSTROKEDARK,
        gridStroke: AXISGRIDSTROKEDARK,
        xLabelStroke: LABELSTROKEDARK,
        yLabelStroke: LABELSTROKEDARK,
        titleFont: TITLEFONT,
    },
    tooltip: {
        lineStroke: VERTICAL_GUIDE_STROKE_DARK,
        inactiveLineStroke: '#666',
        backgroundColor: 'rgba(64, 64, 64, 0.85)',
        color: 'white',
        secondaryColor: '#999',
    },
    pin: {
        iconStroke: 'white',
        lineStroke: 'white',
        dotFill: '#bdbdbd',
    },
    heatmap: {
        axisStroke: '#fafafa',
        axisFontSize: HEATMAP_FONT_SIZE,
        colorScheme: DARK_HEATMAP_COLORS,
    },
    annotationIcon: {
        customEventIconColor: '#818285',
        alertIconColor: DARK_SEVERITY_COLORS,
    },
    alertBar: {
        backgroundColor: '#333333',
        severityColors: DARK_SEVERITY_COLORS,
        spacingColor: SPACINGCOLORDARK,
    },
    rangeSelection: {
        overlayBackgroundColor: 'rgba(0, 0, 0, 0.8)',
        overlayBorderColor: '#97989B',
    },
};

export function getVizThemeForKey(key: VizThemeKey): VizTheme {
    switch (key) {
        case 'dark': {
            return darkVizTheme;
        }
        case 'light':
        default: {
            return lightVizTheme;
        }
    }
}

const VizThemeContextProvider: React.FC<{ value: VizTheme; children: React.ReactNode }> = props => (
    <SplunkThemeProvider colorScheme={props.value.name}>{props.children}</SplunkThemeProvider>
);

/**
 * @deprecated
 *
 * Use `@splunk/themes` instead.
 *
 * For backwards compatibility reasons, `VizThemeContext.Provider` can still be used and
 * will internally delegate to the `@splunk/themes` provider.
 */
export const VizThemeContext = {
    Provider: VizThemeContextProvider,
};

export function useVizTheme(): VizTheme {
    const { colorScheme } = useSplunkTheme();
    return getVizThemeForKey(colorScheme);
}
